/**
 * Routes native push-notification action-button taps (Trash / Reply on the
 * "new email" category) into the app.
 *
 * The action buttons themselves are registered client-side as a
 * `UNNotificationCategory` in lighthouse-mobile's AppDelegate.swift; the
 * backend opts a push into that category via `category: "INBOX_REPLY"` in
 * its payload (see lighthouse-backend's native_push.py /
 * notify_new_email.py), which FCM forwards to APNs as
 * `apns.payload.aps.category`.
 *
 * Trash (`TRASH_ACTION`) is registered without `.foreground`, so iOS runs
 * this listener in the background without ever showing the app UI — it's
 * handled entirely here via the same authenticated API calls the in-app
 * trash button uses (Gmail label PATCH / Outlook folder move). The WebView's
 * Supabase session persists in localStorage across process death, so this
 * works even from a cold/terminated launch.
 *
 * Reply (`REPLY_ACTION`) still opens the app (needs to show the thread /
 * compose UI), so it's stashed + routed as before.
 *
 * Scoped to the "new email" push for now — see the plan's sequencing notes
 * for expanding to other push types.
 *
 * Pending Reply actions are also stashed in sessionStorage so Inbox can still
 * consume them if the router navigation races (cold start, already on
 * inbox, thread watch not re-firing, etc.).
 *
 * Agent pushes (`kind: 'agent_message'`, `conversation_id`, `url`, and since
 * the needs-you pushes `reason: 'approval' | 'question'`) open the
 * conversation at `/dashboard/conversations/<id>`. `pushTargetFromData` is the
 * one place a payload becomes a destination (unit-tested).
 *
 * In the foreground (`pushNotificationReceived`) the agent queue refreshes and
 * a small in-app banner offers the same destination (src/native/inAppBanner.ts),
 * unless the push is about the conversation already on screen.
 */
import type { Router } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { isNativePlatform } from './platform'
import { refreshAgentQueue } from '@/agent/useAgentQueueCounts'
import { showInAppBanner } from '@/native/inAppBanner'
import { haptic } from '@/native/haptics'

const PENDING_PUSH_ACTION_KEY = 'lh.pendingPushAction'

export type PendingPushAction = {
    action: 'reply'
    threadId: string
    /** Text typed into the notification's "Prompt Reply" input, if any. */
    replyPrompt?: string
}

let initialized = false

function readDataBag(data: Record<string, unknown> | undefined | null): Record<string, unknown> {
    if (!data || typeof data !== 'object') return {}
    // Some FCM/APNs paths nest custom keys under an inner `data` object or
    // deliver them as a JSON string.
    const nested = data.data
    if (typeof nested === 'string') {
        try {
            const parsed = JSON.parse(nested)
            if (parsed && typeof parsed === 'object') {
                return { ...data, ...(parsed as Record<string, unknown>) }
            }
        } catch {
            // ignore
        }
    } else if (nested && typeof nested === 'object') {
        return { ...data, ...(nested as Record<string, unknown>) }
    }
    return data
}

function threadIdFromNotificationData(data: Record<string, unknown>): string | null {
    const flat = readDataBag(data)
    const raw = flat.thread_id ?? flat.threadId ?? flat.thread
    if (typeof raw === 'string' && raw.trim()) return raw.trim()
    return null
}

const FEEDBACK_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function feedbackIdFromNotificationData(data: Record<string, unknown>): string | null {
    const flat = readDataBag(data)
    const raw = flat.feedback_id ?? flat.feedbackId
    if (typeof raw === 'string' && FEEDBACK_ID_RE.test(raw.trim())) return raw.trim()
    const url = flat.url
    if (typeof url === 'string') {
        try {
            const parsed = new URL(url, 'https://www.joinlighthouse.ai')
            const q = parsed.searchParams.get('feedback')
            if (q && FEEDBACK_ID_RE.test(q)) return q
        } catch {
            // ignore
        }
    }
    return null
}

const CONVERSATION_PATH_RE = /^\/dashboard\/conversations\/([^/?#]+)/

/** Where a push should take the creator. */
export type PushTarget =
    | { kind: 'trash'; threadId: string }
    | { kind: 'reply'; threadId: string; replyPrompt?: string }
    | { kind: 'feedback'; feedbackId: string }
    | { kind: 'conversation'; conversationId: string; reason?: 'approval' | 'question' }
    | { kind: 'path'; path: string }

function str(v: unknown): string | null {
    return typeof v === 'string' && v.trim() ? v.trim() : null
}

/**
 * Turn a push payload (and the action button tapped, if any) into a destination. Agent pushes win over the
 * generic `url`, and a `url` that names a conversation is treated as one.
 */
export function pushTargetFromData(
    raw: Record<string, unknown> | null | undefined,
    actionId?: string,
    inputValue?: unknown,
): PushTarget | null {
    const data = readDataBag(raw)
    const conversationFromUrl = (() => {
        const url = str(data.url)
        const m = url ? CONVERSATION_PATH_RE.exec(url) : null
        return m ? decodeURIComponent(m[1]) : null
    })()
    const reasonRaw = str(data.reason)
    const reason = reasonRaw === 'approval' || reasonRaw === 'question' ? reasonRaw : undefined

    if (data.kind === 'agent_message') {
        const conversationId = str(data.conversation_id) ?? str(data.conversationId) ?? conversationFromUrl
        if (conversationId) return { kind: 'conversation', conversationId, ...(reason ? { reason } : {}) }
    }

    const threadId = threadIdFromNotificationData(data)
    if (threadId) {
        if (actionId === 'TRASH_ACTION') return { kind: 'trash', threadId }
        const replyPrompt = str(inputValue) ?? undefined
        return { kind: 'reply', threadId, ...(replyPrompt ? { replyPrompt } : {}) }
    }

    const feedbackId = feedbackIdFromNotificationData(data)
    if (feedbackId) return { kind: 'feedback', feedbackId }

    if (conversationFromUrl) return { kind: 'conversation', conversationId: conversationFromUrl, ...(reason ? { reason } : {}) }
    const url = str(data.url)
    if (url && url.startsWith('/') && !url.startsWith('//')) return { kind: 'path', path: url }
    return null
}

/** The conversation a push (or a delivered notification) is about, if any. */
export function conversationIdFromPushData(raw: Record<string, unknown> | null | undefined): string | null {
    const target = pushTargetFromData(raw)
    return target?.kind === 'conversation' ? target.conversationId : null
}

export function stashPendingPushAction(pending: PendingPushAction) {
    try {
        sessionStorage.setItem(PENDING_PUSH_ACTION_KEY, JSON.stringify(pending))
    } catch {
        // ignore quota / private mode
    }
}

export function consumePendingPushAction(): PendingPushAction | null {
    try {
        const raw = sessionStorage.getItem(PENDING_PUSH_ACTION_KEY)
        if (!raw) return null
        sessionStorage.removeItem(PENDING_PUSH_ACTION_KEY)
        const parsed = JSON.parse(raw) as PendingPushAction
        if (!parsed?.threadId || !parsed?.action) return null
        return parsed
    } catch {
        return null
    }
}

export function peekPendingPushAction(): PendingPushAction | null {
    try {
        const raw = sessionStorage.getItem(PENDING_PUSH_ACTION_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as PendingPushAction
        if (!parsed?.threadId || !parsed?.action) return null
        return parsed
    } catch {
        return null
    }
}

/**
 * Trash a thread headlessly — no navigation, no UI shown. Mirrors the
 * non-toggle half of Inbox.vue's `handleInlineThreadAction('trash')`
 * branch: Gmail adds the `TRASH` label, Outlook moves to Deleted Items.
 */
async function trashThreadInBackground(threadId: string) {
    try {
        // Loaded on use: the inbox API and mail store stay out of the app's entry chunk.
        const [{ useGetSingleThread }, { default: useEmail }] = await Promise.all([
            import('@/api/inbox.api'),
            import('@/composables/useEmail'),
        ])
        const result = await useGetSingleThread(threadId)
        if (!result.ok) return
        const thread = result.data
        const messages: any[] = thread?.messages || []
        if (!messages.length) return

        const { updateMessageLabels, moveOutlookMessages } = useEmail()
        const isOutlook = thread?.last_message?.provider === 'outlook'
            || messages[0]?.provider === 'outlook'

        if (isOutlook) {
            const providerIds = messages
                .map((m: any) => m.provider_id)
                .filter((id: unknown): id is string => typeof id === 'string' && !!id)
            await Promise.all(
                providerIds.map((providerId: string) =>
                    moveOutlookMessages({ message_ids: [providerId], destination: 'deleteditems' }),
                ),
            )
        } else {
            const messageIds = messages.map((m: any) => ({ id: m.id, provider_id: m.provider_id }))
            await updateMessageLabels(messageIds, ['TRASH'], null)
        }
    } catch {
        // No UI in this flow to surface a failure to — the notification has
        // already been dismissed by the time this runs.
    }
}

/** Take the creator where a push points. */
export function openPushTarget(router: Router, target: PushTarget) {
    switch (target.kind) {
        case 'trash':
            void trashThreadInBackground(target.threadId)
            return
        case 'reply': {
            stashPendingPushAction({ action: 'reply', threadId: target.threadId, replyPrompt: target.replyPrompt })
            void router.push({
                name: 'dashboard',
                params: { section: 'inbox' },
                query: {
                    thread: target.threadId,
                    pushReply: '1',
                    ...(target.replyPrompt ? { pushReplyPrompt: target.replyPrompt } : {}),
                },
            })
            return
        }
        case 'feedback':
            void router.push({ name: 'dashboard', params: { section: 'home' }, query: { feedback: target.feedbackId } })
            return
        case 'conversation':
            void router.push(`/dashboard/conversations/${encodeURIComponent(target.conversationId)}`)
            return
        case 'path':
            void router.push(target.path)
    }
}

/** The conversation on screen right now (phone chat on Home doesn't count: it isn't routed). */
function openConversationId(router: Router): string | null {
    const id = router.currentRoute.value.params?.conversationId
    return typeof id === 'string' && id ? id : null
}

function onForegroundPush(router: Router, notification: { title?: string; subtitle?: string; body?: string; data?: unknown }) {
    const data = readDataBag((notification.data || {}) as Record<string, unknown>)
    const target = pushTargetFromData(data)
    if (data.kind === 'agent_message' || target?.kind === 'conversation') void refreshAgentQueue()
    // Already looking at it: the live stream shows the change; a banner would only repeat it.
    if (target?.kind === 'conversation' && target.conversationId === openConversationId(router)) return
    const title = str(notification.title) ?? str(data.title)
    const body = str(notification.body) ?? str(data.body)
    if (!title && !body) return
    showInAppBanner({
        title: title ?? 'Lighthouse',
        subtitle: str(notification.subtitle) ?? str(data.subtitle) ?? undefined,
        body: body ?? undefined,
        tone: target?.kind === 'conversation' && target.reason ? 'attention' : 'default',
        onOpen: target && target.kind !== 'trash' ? () => openPushTarget(router, target) : undefined,
    })
    haptic('light')
}

export async function initNativePushActionRouting(router: Router) {
    if (!isNativePlatform() || initialized || !Capacitor.isPluginAvailable('PushNotifications')) return
    initialized = true

    const { PushNotifications } = await import('@capacitor/push-notifications')
    void PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        // iOS delivers the typed text-input value as `action.inputValue` for
        // `UNTextInputNotificationAction` categories (REPLY_ACTION here).
        const inputValue = (action as unknown as { inputValue?: unknown }).inputValue
        const target = pushTargetFromData(
            (action.notification?.data || {}) as Record<string, unknown>,
            action.actionId,
            inputValue,
        )
        if (target) openPushTarget(router, target)
    })
    void PushNotifications.addListener('pushNotificationReceived', (notification) => {
        onForegroundPush(router, notification)
    })
}

/**
 * Remove the delivered notifications about a conversation (the creator just opened it), so Notification Center
 * doesn't keep asking about something they've seen.
 */
export async function clearConversationNotifications(conversationId: string): Promise<void> {
    if (!isNativePlatform() || !conversationId || !Capacitor.isPluginAvailable('PushNotifications')) return
    try {
        const { PushNotifications } = await import('@capacitor/push-notifications')
        const { notifications } = await PushNotifications.getDeliveredNotifications()
        const mine = notifications.filter((n) => conversationIdFromPushData((n.data || {}) as Record<string, unknown>) === conversationId)
        if (mine.length) await PushNotifications.removeDeliveredNotifications({ notifications: mine })
    } catch {
        // Older plugin, or nothing delivered.
    }
}
